import { useEffect, useRef, useState } from "react";
import { Bot, ChevronLeft, Loader2, Send } from "lucide-react";

import type { AiGroupMessage, AiTextBlock, AiThoughtBlock, ChatMessage } from "./types";
import { streamStrategiesChat } from "./api/strategiesChat";
import { mapSSEEventToAiBlocks } from "./utils/eventMapper";
import { FlowCanvas } from "./components/flow/FlowCanvas";
import { StrategiesBacktestView } from "./components/backtest/StrategiesBacktestView";
import { StrategyCodeView } from "./components/codegen/StrategyCodeView";
import { ExpandableThought } from "./components/chat/ExpandableThought";
import { MarketAssistPanel } from "./components/chat/MarketAssistPanel";
import { ToolCall } from "./components/chat/ToolCall";
import { MarkdownRenderer } from "../../features/kquant/components/MarkdownRenderer";
import { UserMessage } from "./components/chat/UserMessage";
import { QuestionnaireWidget } from "./components/questionnaire/QuestionnaireWidget";
import type { QuestionnaireSubmitPayload } from "./types";
import type { MarketAnalysisResult } from "./tools/market_analysis";

function isAiGroupMessage(m: ChatMessage): m is AiGroupMessage {
  return m.type === "ai-group";
}

export default function StrategiesDemoApp({
  initialPrompt,
}: {
  initialPrompt?: string;
}) {
  const [sessionId, setSessionId] = useState(() => {
    if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
    return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  });
  const sessionIdRef = useRef(sessionId);
  useEffect(() => {
    sessionIdRef.current = sessionId;
  }, [sessionId]);

  const [layout, setLayout] = useState<"center" | "split">("center");
  const layoutRef = useRef<"center" | "split">("center");
  const [inputValue, setInputValue] = useState("");
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [currentFlowStep, setCurrentFlowStep] = useState(0);
  const [strategyType, setStrategyType] = useState<"dual_ma" | "ai_trading">("dual_ma");
  const strategyTypeRef = useRef<"dual_ma" | "ai_trading">("dual_ma");
  const [isInputDisabled, setIsInputDisabled] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);

  const [leftPane, setLeftPane] = useState<"flow" | "backtest" | "codegen">("flow");
  const [backtestStatus, setBacktestStatus] = useState<"idle" | "running" | "done">("idle");
  const [backtestScenario, setBacktestScenario] = useState<"baseline" | "sentiment" | "ai_baseline" | "ai_sentiment">("baseline");
  const [selectedSymbol, setSelectedSymbol] = useState<string>("Binance.BTC_USDT");
  const didAutoBacktestRef = useRef(false);
  const pendingBacktestScenarioRef = useRef<"baseline" | "sentiment" | "ai_baseline" | "ai_sentiment">("baseline");
  const pendingRevealDoneRef = useRef<(() => void) | null>(null);
  // Tracks which scenario keys have already completed the code reveal animation
  const codeRevealedRef = useRef<Set<string>>(new Set());
  // True when startBacktestFlow triggered the codegen pane (first-time animation should play)
  const codeAnimationActiveRef = useRef(false);
  const waitingBacktestResumeGroupIdRef = useRef<number | null>(null);
  const didAutofillInitialDemoPromptRef = useRef(false);
  const didAutofillRoundDemoPromptRef = useRef(false);
  const [isDemoPrefillActive, setIsDemoPrefillActive] = useState(false);
  const [lockedMarketAssistIds, setLockedMarketAssistIds] = useState<Record<number, true>>({});
  const messageIdSeqRef = useRef<number>(Date.now());

  const CHAT_SIDEBAR_DEFAULT_WIDTH = 400;
  const CHAT_SIDEBAR_MIN_WIDTH = 320;
  const CHAT_SIDEBAR_MAX_WIDTH = 720;
  const CHAT_SIDEBAR_STORAGE_KEY = "strategies.chatSidebarWidth";
  const BACKTEST_RUN_MS = 5000;

  const swallow = (_err: unknown) => undefined;

  const [isChatCollapsed, setIsChatCollapsed] = useState(false);
  const [chatSidebarWidth, setChatSidebarWidth] = useState(() => {
    if (typeof window === "undefined") return CHAT_SIDEBAR_DEFAULT_WIDTH;
    try {
      const raw = window.localStorage.getItem(CHAT_SIDEBAR_STORAGE_KEY);
      const n = raw ? Number(raw) : NaN;
      if (!Number.isFinite(n)) return CHAT_SIDEBAR_DEFAULT_WIDTH;
      return Math.min(CHAT_SIDEBAR_MAX_WIDTH, Math.max(CHAT_SIDEBAR_MIN_WIDTH, n));
    } catch {
      return CHAT_SIDEBAR_DEFAULT_WIDTH;
    }
  });
  const chatSidebarWidthRef = useRef(chatSidebarWidth);
  const lastExpandedChatWidthRef = useRef(chatSidebarWidth);
  const chatResizeRef = useRef<{ startX: number; startWidth: number; lastWidth: number } | null>(
    null
  );
  const [isChatResizing, setIsChatResizing] = useState(false);

  const initialDemoPrompt =
    "I'm a beginner in quantitative trading; help me create a simple trading strategy.";
  const sentimentDemoPrompt = "Please add a news sentiment factor analysis to this strategy.";
  const aiTradingSentimentPrompt = "请为这个策略加入情绪增强节点，完善Telegram推送和交易执行模块";

  const lockMarketAssist = (assistId: number) => {
    setLockedMarketAssistIds((prev) => (prev[assistId] ? prev : { ...prev, [assistId]: true }));
  };

  const autoAdvanceRef = useRef<Record<string, true>>({});
  const timersRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const messagesRef = useRef<ChatMessage[]>(messages);
  const missingGroupRetriesRef = useRef<Record<string, number>>({});
  const missingGroupMaxTries = 10;
  const activeStreamGroupIdRef = useRef<number | null>(null);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  useEffect(() => {
    const groupId = activeStreamGroupIdRef.current;
    if (!groupId) return;

    const group = messages.find((m): m is AiGroupMessage => isAiGroupMessage(m) && m.id === groupId);
    if (!group) return;

    const visibleCount = group.visibleCount ?? 0;
    const total = Array.isArray(group.blocks) ? group.blocks.length : 0;
    if (total <= 0) return;
    if (visibleCount >= total) return;
    if (waitingBacktestResumeGroupIdRef.current === groupId) return;

    if (visibleCount > 0) {
      const lastVisibleIdx = visibleCount - 1;
      const lastVisible = group.blocks[lastVisibleIdx];
      if (lastVisible?.type === "questionnaire" || lastVisible?.type === "market-assist") return;
    }

    const hasPendingReveal =
      !!timersRef.current[`ai:${groupId}:delay`] || !!timersRef.current[`ai:${groupId}:reveal`];
    if (hasPendingReveal) return;

    queueRevealNextRef.current?.(groupId, 0, 0);
  }, [messages]);

  useEffect(() => {
    if (layout !== "center") return;
    if (messages.length !== 0) return;
    if (didAutofillInitialDemoPromptRef.current) return;
    if (inputValue.trim()) return;

    didAutofillInitialDemoPromptRef.current = true;
    const nextPrompt = (initialPrompt ?? "").trim() ? String(initialPrompt).trim() : initialDemoPrompt;
    setInputValue(nextPrompt);
    setIsDemoPrefillActive(nextPrompt === initialDemoPrompt);
  }, [initialDemoPrompt, initialPrompt, inputValue, layout, messages.length]);

  useEffect(() => {
    chatSidebarWidthRef.current = chatSidebarWidth;
    if (!isChatCollapsed && chatSidebarWidth >= CHAT_SIDEBAR_MIN_WIDTH) {
      lastExpandedChatWidthRef.current = chatSidebarWidth;
    }
  }, [chatSidebarWidth, isChatCollapsed]);

  useEffect(() => {
    if (!isChatResizing) return;

    const onPointerMove = (e: PointerEvent) => {
      const state = chatResizeRef.current;
      if (!state) return;
      const deltaX = e.clientX - state.startX;
      const nextWidth = state.startWidth - deltaX;
      const clamped = Math.max(0, Math.min(CHAT_SIDEBAR_MAX_WIDTH, nextWidth));
      state.lastWidth = clamped;
      setChatSidebarWidth(clamped);
    };

    const endResize = () => {
      const lastWidth = chatResizeRef.current?.lastWidth ?? chatSidebarWidthRef.current;
      setIsChatResizing(false);
      chatResizeRef.current = null;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";

      if (lastWidth < CHAT_SIDEBAR_MIN_WIDTH) {
        setIsChatCollapsed(true);
        return;
      }

      const finalWidth = Math.min(CHAT_SIDEBAR_MAX_WIDTH, Math.max(CHAT_SIDEBAR_MIN_WIDTH, lastWidth));
      setChatSidebarWidth(finalWidth);
      try {
        window.localStorage.setItem(CHAT_SIDEBAR_STORAGE_KEY, String(finalWidth));
      } catch (err) {
        swallow(err);
      }
    };

    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", endResize);
    window.addEventListener("pointercancel", endResize);
    return () => {
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", endResize);
      window.removeEventListener("pointercancel", endResize);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
  }, [isChatResizing]);

  const clearTimer = (key: string) => {
    const t = timersRef.current[key];
    if (!t) return;
    clearTimeout(t);
    delete timersRef.current[key];
  };

  const schedule = (key: string, ms: number, fn: () => void) => {
    clearTimer(key);
    timersRef.current[key] = setTimeout(fn, ms);
  };

  useEffect(() => {
    return () => {
      Object.keys(timersRef.current).forEach((k) => {
        clearTimeout(timersRef.current[k]);
      });
      timersRef.current = {};
    };
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, layout]);


  const setGroupThinking = (groupId: number, isThinking: boolean) => {
    setMessages((prev) =>
      prev.map((m) =>
        m.type === "ai-group" && m.id === groupId ? { ...m, isThinking } : m
      )
    );
  };

  const queueRevealNextRef = useRef<
    ((groupId: number, delayMs?: number, thinkingMs?: number) => void) | null
  >(null);

  const revealNextBlock = (groupId: number) => {
    if (waitingBacktestResumeGroupIdRef.current === groupId) {
      setGroupThinking(groupId, false);
      return;
    }

    const group = messagesRef.current.find(
      (m): m is AiGroupMessage => isAiGroupMessage(m) && m.id === groupId
    );
    if (!group) {
      const triesKey = String(groupId);
      const tries = (missingGroupRetriesRef.current[triesKey] ?? 0) + 1;
      missingGroupRetriesRef.current[triesKey] = tries;
      if (tries <= missingGroupMaxTries) {
        queueRevealNextRef.current?.(groupId, 50, 0);
      }
      return;
    }
    delete missingGroupRetriesRef.current[String(groupId)];

    const visibleCount = group.visibleCount ?? 0;
    if (!Array.isArray(group.blocks) || visibleCount >= group.blocks.length) {
      setGroupThinking(groupId, false);
      return;
    }

    const block = group.blocks[visibleCount];
    const index = visibleCount;
    const total = group.blocks.length;

    setMessages((prev) =>
      prev.map((m) =>
        m.type === "ai-group" && m.id === groupId
          ? { ...m, visibleCount: (m.visibleCount ?? 0) + 1, isThinking: false }
          : m
      )
    );

    if ((block.type === "thought" || block.type === "tool") && typeof block.flowStep === "number") {
      const stepToSet = block.flowStep;
      if (layoutRef.current === "center") {
        layoutRef.current = "split";
        setLayout("split");
        setCurrentFlowStep(stepToSet);
      } else {
        setCurrentFlowStep(stepToSet);
      }
    }
    if ((block.type === "thought" || block.type === "tool") && block.enableInput) {
      setIsInputDisabled(false);
    }

    const hasMore = index + 1 < total;
    let backtestScenarioToRun: "baseline" | "sentiment" | "ai_baseline" | "ai_sentiment" | null = null;
    if (block.type === "tool") {
      if (block.triggerBacktestScenario) {
        backtestScenarioToRun = block.triggerBacktestScenario;
      } else if (block.triggerBacktest && !didAutoBacktestRef.current) {
        didAutoBacktestRef.current = true;
        backtestScenarioToRun = "baseline";
      }
    }

    if (backtestScenarioToRun) {
      pendingBacktestScenarioRef.current = backtestScenarioToRun;

      waitingBacktestResumeGroupIdRef.current = groupId;
      clearTimer(`ai:${groupId}:delay`);
      clearTimer(`ai:${groupId}:reveal`);
      startBacktestFlow(() => {
        if (waitingBacktestResumeGroupIdRef.current !== groupId) return;
        waitingBacktestResumeGroupIdRef.current = null;

        const latestGroup = messagesRef.current.find(
          (m): m is AiGroupMessage => isAiGroupMessage(m) && m.id === groupId
        );
        const nextVisible = latestGroup?.visibleCount ?? 0;
        const nextTotal = Array.isArray(latestGroup?.blocks) ? latestGroup!.blocks.length : 0;
        if (nextTotal > 0 && nextVisible < nextTotal) {
          queueRevealNextRef.current?.(groupId, 250, 350);
        }
      });
    }

    if (!hasMore) {
      setGroupThinking(groupId, false);
      return;
    }

    if (backtestScenarioToRun) {
      return;
    }

    if (block.type === "thought" || block.type === "tool") {
      const advanceKey = `${groupId}-${index}`;
      autoAdvanceRef.current[advanceKey] = true;
      queueRevealNextRef.current?.(groupId, block.autoAdvanceMs ?? 650, 450);
    } else if (block.type === "text" || block.type === "questionnaire" || block.type === "market-assist") {
      // Text, questionnaire, and market-assist blocks reveal immediately
      queueRevealNextRef.current?.(groupId, 0, 0);
    }
  };

  useEffect(() => {
    messages.forEach((m) => {
      if (m.type !== "ai-group") return;
      const groupId = m.id;
      const visibleCount = m.visibleCount ?? 0;
      if (!Array.isArray(m.blocks) || visibleCount <= 0) return;
      if (visibleCount >= m.blocks.length) return;
      if (waitingBacktestResumeGroupIdRef.current === groupId) return;

      const lastIdx = visibleCount - 1;
      const lastBlock = m.blocks[lastIdx];
      if (lastBlock.type !== "thought" && lastBlock.type !== "tool") return;

      const advanceKey = `${groupId}-${lastIdx}`;
      if (autoAdvanceRef.current[advanceKey]) return;
      autoAdvanceRef.current[advanceKey] = true;

      queueRevealNextRef.current?.(groupId, lastBlock.autoAdvanceMs ?? 650, 450);
    });
  }, [messages]);

  // Debug hook: sets AI strategy to a specific step via property setter
  //   window.ai_strategy_debug = true  → step 1
  //   window.ai_strategy_debug = 2     → step 2
  //   window.ai_strategy_debug = 3     → step 3
  //   window.ai_strategy_debug = 4     → step 4
  useEffect(() => {
    const applyStep = (val: unknown) => {
      if (val !== true && (typeof val !== "number" || val < 1)) return;
      const step = typeof val === "number" ? Math.min(Math.max(Math.floor(val), 1), 4) : 1;
      setStrategyType("ai_trading");
      strategyTypeRef.current = "ai_trading";
      setLayout("split");
      layoutRef.current = "split";
      setLeftPane("flow");
      setCurrentFlowStep(step);
      setBacktestStatus("idle");
    };
    try {
      Object.defineProperty(window, "ai_strategy_debug", {
        configurable: true,
        set(val: unknown) { applyStep(val); },
        get() { return undefined; },
      });
    } catch {
      // fallback: RAF poll (e.g. already defined)
      let rafId: number;
      const check = () => {
        const w = window as unknown as Record<string, unknown>;
        const val = w.ai_strategy_debug;
        if (val === true || (typeof val === "number" && val >= 1)) {
          w.ai_strategy_debug = undefined;
          applyStep(val);
        }
        rafId = requestAnimationFrame(check);
      };
      rafId = requestAnimationFrame(check);
      return () => cancelAnimationFrame(rafId);
    }
    return () => {
      try { Object.defineProperty(window, "ai_strategy_debug", { configurable: true, value: undefined, writable: true }); } catch { /* noop */ }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);




  queueRevealNextRef.current = (groupId: number, delayMs = 300, thinkingMs = 450) => {
    schedule(`ai:${groupId}:delay`, delayMs, () => {
      setGroupThinking(groupId, true);
      schedule(`ai:${groupId}:reveal`, thinkingMs, () => {
        revealNextBlock(groupId);
      });
    });
  };


  const send = (overrideMessage?: string, marketSelection?: MarketAnalysisResult) => {
    const message = (overrideMessage ?? inputValue).trim();
    if (!message || isInputDisabled || isStreaming) return;

    const now = ++messageIdSeqRef.current;
    const groupId = ++messageIdSeqRef.current;
    const userMsg = message;

    setIsDemoPrefillActive(false);

    setMessages((prev) => [
      ...prev,
      { id: now, type: "user", content: userMsg },
      {
        id: groupId,
        type: "ai-group",
        isThinking: true,
        visibleCount: 0,
        blocks: [],
      },
    ]);

    if (overrideMessage === undefined) setInputValue("");
    setIsInputDisabled(true);
    setIsStreaming(true);
    activeStreamGroupIdRef.current = groupId;
    
    // Track thinking time
    let thinkingStartTime: number | null = null;
    let lastEventType: string | null = null;
    let thinkingTimerInterval: NodeJS.Timeout | null = null;
    void (async () => {
      try {
        await streamStrategiesChat(sessionIdRef.current, userMsg, marketSelection, (event) => {
          if (activeStreamGroupIdRef.current !== groupId) return;

          const eventType = (event as { type?: unknown }).type;
          
          // Track thinking time
          if (eventType === "thinking" && thinkingStartTime === null) {
            thinkingStartTime = Date.now();
            
            // Start real-time timer to update thinking time every 100ms
            thinkingTimerInterval = setInterval(() => {
              if (thinkingStartTime === null) return;
              
              const elapsedMs = Date.now() - thinkingStartTime;
              const elapsedSec = (elapsedMs / 1000).toFixed(1);
              
              setMessages((prev) =>
                prev.map((m) => {
                  if (m.type !== "ai-group" || m.id !== groupId) return m;
                  const blocks = Array.isArray(m.blocks) ? m.blocks : [];
                  const lastIdx = blocks.length - 1;
                  const lastBlock = blocks[lastIdx];
                  
                  if (lastBlock?.type === "thought") {
                    const updatedBlocks = blocks.map((b, i) =>
                      i === lastIdx && b.type === "thought"
                        ? ({ ...b, time: `${elapsedSec}s` } satisfies AiThoughtBlock)
                        : b
                    );
                    return { ...m, blocks: updatedBlocks };
                  }
                  return m;
                })
              );
            }, 100);
          }
          
          // When event type changes from thinking to something else, stop timer
          if (lastEventType === "thinking" && eventType !== "thinking" && thinkingStartTime !== null) {
            // Clear the interval timer
            if (thinkingTimerInterval !== null) {
              clearInterval(thinkingTimerInterval);
              thinkingTimerInterval = null;
            }
            
            // Final update with exact time
            const elapsedMs = Date.now() - thinkingStartTime;
            const elapsedSec = (elapsedMs / 1000).toFixed(1);
            
            setMessages((prev) =>
              prev.map((m) => {
                if (m.type !== "ai-group" || m.id !== groupId) return m;
                const blocks = Array.isArray(m.blocks) ? m.blocks : [];
                const lastIdx = blocks.length - 1;
                const lastBlock = blocks[lastIdx];
                
                if (lastBlock?.type === "thought") {
                  const updatedBlocks = blocks.map((b, i) =>
                    i === lastIdx && b.type === "thought"
                      ? ({ ...b, time: `${elapsedSec}s` } satisfies AiThoughtBlock)
                      : b
                  );
                  return { ...m, blocks: updatedBlocks };
                }
                return m;
              })
            );
            
            thinkingStartTime = null;
          }
          
          lastEventType = typeof eventType === "string" ? eventType : null;
          
          if (eventType === "meta") {
            const sid = (event as { session_id?: unknown }).session_id;
            if (typeof sid === "string") setSessionId(sid);
          }
          
          const mapped = mapSSEEventToAiBlocks(event);
          const blocksToAdd = [...mapped];
          if (eventType === "stage" && (event as { stage?: unknown }).stage === "questionnaire") {
            blocksToAdd.push({ type: "questionnaire" });
          }
          if (blocksToAdd.length > 0) {
            setMessages((prev) =>
              prev.map((m) => {
                if (m.type !== "ai-group" || m.id !== groupId) return m;

                const startingBlocks = Array.isArray(m.blocks) ? m.blocks : [];
                let nextBlocks = startingBlocks;

                for (const blk of blocksToAdd) {
                  if (blk.type === "text") {
                    const lastIdx = nextBlocks.length - 1;
                    const last = nextBlocks[lastIdx];
                    const canAppendToLast = last?.type === "text";

                    if (canAppendToLast) {
                      nextBlocks = nextBlocks.map((b, i) =>
                        i === lastIdx && b.type === "text"
                          ? ({ ...b, content: `${b.content}${blk.content}` } satisfies AiTextBlock)
                          : b
                      );
                      continue;
                    }
                  }

                  // Append to last thought block if it exists
                  if (blk.type === "thought") {
                    const lastIdx = nextBlocks.length - 1;
                    const last = nextBlocks[lastIdx];

                    if (last?.type === "thought") {
                      nextBlocks = nextBlocks.map((b, i) =>
                        i === lastIdx && b.type === "thought"
                          ? ({ ...b, content: `${b.content}${blk.content}` } satisfies AiThoughtBlock)
                          : b
                      );
                      continue;
                    }
                  }

                  nextBlocks = [...nextBlocks, blk];
                }


                return { ...m, blocks: nextBlocks };
              })
            );
          }
          if (eventType === "tool_result") {
            const flowStep = (event as { flowStep?: unknown }).flowStep;

            if (flowStep === 4) {
              setLayout("split");
              layoutRef.current = "split";
              setLeftPane("flow");
            }

          }

          if (eventType === "done") {
            setMessages((prev) =>
              prev.map((m) => (m.type === "ai-group" && m.id === groupId ? { ...m, isThinking: false } : m))
            );
          }
        });
      } catch (err) {
        swallow(err);
        setMessages((prev) =>
          prev.map((m) => (m.type === "ai-group" && m.id === groupId ? { ...m, isThinking: false } : m))
        );
      } finally {
        // Clean up thinking timer
        if (thinkingTimerInterval !== null) {
          clearInterval(thinkingTimerInterval);
          thinkingTimerInterval = null;
        }
        
        if (activeStreamGroupIdRef.current === groupId) {
          activeStreamGroupIdRef.current = null;
        }
        setIsStreaming(false);
        setIsInputDisabled(false);
      }
    })();
  };

  const handleSend = (message: string, marketSelection?: MarketAnalysisResult) => {
    send(message, marketSelection);
  };

  const startWorkflowBuild = (marketSelection?: MarketAnalysisResult) => {
    if (marketSelection) {
      const title = marketSelection.recommended.marketTitle.toLowerCase();
      if (title.includes("crypto") || title.includes("bitcoin") || title.includes("btc")) {
        setSelectedSymbol("Binance.BTC_USDT");
      } else if (title.includes("us stock") || title.includes("equity") || title.includes("stock")) {
        setSelectedSymbol("NYSE.SPY");
      } else if (title.includes("future") || title.includes("futures")) {
        setSelectedSymbol("CME.ES");
      } else {
        setSelectedSymbol("Binance.BTC_USDT");
      }
      // Detect strategy type from market selection
      const riskTitle = marketSelection.user?.riskTitle || "";
      const returnTitle = marketSelection.user?.returnTitle || "";
      const isHighRisk = riskTitle.includes("risk_high");
      const isHighReturn = returnTitle.includes("ret_high");
      if (isHighRisk && isHighReturn) {
        setStrategyType("ai_trading");
        strategyTypeRef.current = "ai_trading";
      } else {
        setStrategyType("dual_ma");
        strategyTypeRef.current = "dual_ma";
      }
    }
    setCurrentFlowStep(0);
    setLayout("split");
    layoutRef.current = "split";
    setLeftPane("flow");
    setBacktestStatus("idle");
    setBacktestScenario("baseline");
    pendingBacktestScenarioRef.current = "baseline";
    didAutoBacktestRef.current = false;
    didAutofillInitialDemoPromptRef.current = false;
    didAutofillRoundDemoPromptRef.current = false;
    setIsDemoPrefillActive(false);
    // Scroll to bottom after layout change
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 100);
    setTimeout(() => {
      handleSend("I've completed my preferences form, please continue.", marketSelection);
    }, 500);
  };

  const handleQuestionnaireSubmit = (payload: QuestionnaireSubmitPayload) => {
    const helpSelect = payload.answers.market.includes("help_select");
    if (!helpSelect) {
      const marketAnswers = payload.answers.market;
      if (marketAnswers.includes("crypto_247")) {
        setSelectedSymbol("Binance.BTC_USDT");
      } else if (marketAnswers.includes("us_daily")) {
        setSelectedSymbol("NYSE.SPY");
      } else if (marketAnswers.includes("futures_intraday")) {
        setSelectedSymbol("CME.ES");
      } else if (marketAnswers.includes("custom_market")) {
        const custom = payload.customInputs["custom_market"];
        setSelectedSymbol(custom?.trim() || "Binance.BTC_USDT");
      }
      // Detect high-risk + high-return for AI trading strategy
      const riskAnswers = payload.answers.risk || [];
      const returnAnswers = payload.answers.return || [];
      const isHighRisk = riskAnswers.some((r) => r.includes("risk_high"));
      const isHighReturn = returnAnswers.some((r) => r.includes("ret_high"));
      if (isHighRisk && isHighReturn) {
        setStrategyType("ai_trading");
        strategyTypeRef.current = "ai_trading";
      } else {
        setStrategyType("dual_ma");
        strategyTypeRef.current = "dual_ma";
      }
      startWorkflowBuild();
      return;
    }

    const groupId = Date.now();
    const assistId = groupId + 7;
    setCurrentFlowStep(0);

    setMessages((prev) => [
      ...prev,
      {
        id: groupId,
        type: "ai-group",
        isThinking: false,
        visibleCount: 1,
        blocks: [
          {
            type: "market-assist",
            assistId,
            payload,
          },
        ],
      },
    ]);
  };

  const startBacktestFlow = (onBacktestDone?: () => void) => {
    const scenario = pendingBacktestScenarioRef.current;
    setLayout("split");
    layoutRef.current = "split";
    // Show code generation tab first; wait for lines to finish revealing,
    // then hold 2 more seconds before switching to backtest.
    codeAnimationActiveRef.current = true;
    setLeftPane("codegen");
    setBacktestScenario(scenario);
    setBacktestStatus("idle");
    // onRevealComplete (called by StrategyCodeView) triggers the 2s hold + backtest switch.
    // Store it so the callback can be set on the component.
    pendingRevealDoneRef.current = () => {
      schedule("codegen:hold", 2000, () => {
        setLeftPane("backtest");
        setBacktestStatus("running");
        schedule("backtest:done", BACKTEST_RUN_MS, () => {
          setBacktestStatus("done");
          if (scenario === "baseline") {
            didAutofillRoundDemoPromptRef.current = false;
            setInputValue((prev) => {
              if (prev.trim()) return prev;
              didAutofillRoundDemoPromptRef.current = true;
              setIsDemoPrefillActive(true);
              return sentimentDemoPrompt;
            });
          } else if (scenario === "ai_baseline") {
            didAutofillRoundDemoPromptRef.current = false;
            setInputValue((prev) => {
              if (prev.trim()) return prev;
              didAutofillRoundDemoPromptRef.current = true;
              setIsDemoPrefillActive(true);
              return aiTradingSentimentPrompt;
            });
          }
          onBacktestDone?.();
        });
      });
    };
  };

  const handleDemoPromptFocus = () => {
    if (!isDemoPrefillActive) return;
    setIsDemoPrefillActive(false);
  };

  const renderMessages = () => {
    if (messages.length === 0) {
      return (
        <div className="h-full flex flex-col items-center justify-center text-center opacity-40 mt-20">
          <Bot size={48} className="mb-4 text-gray-500" />
          <p className="text-[15px] font-medium">
            How can I help you build your strategy today?
          </p>
          <p className="text-[13px] mt-2 max-w-xs">
            Type a prompt below.
          </p>
        </div>
      );
    }

    const lastAiGroupId = [...messages].reverse().find(isAiGroupMessage)?.id;

    return messages.map((msg) => {
      if (msg.type === "user") {
        return <UserMessage key={msg.id} content={msg.content} />;
      }

      if (msg.type === "ai-group") {
        const visibleCount = msg.visibleCount ?? msg.blocks.length;
        const visibleBlocks = msg.blocks.slice(0, visibleCount);
        return (
          <div key={msg.id} className="flex w-full items-start gap-3 mb-6">
            <div className="w-8 h-8 rounded-full border border-gray-200 bg-white flex items-center justify-center text-gray-800 shrink-0 shadow-sm mt-1">
              <Bot size={18} />
            </div>
            <div className="flex-1 pt-1.5 flex flex-col min-w-0">
              {visibleBlocks.map((block, idx) => {
                if (block.type === "thought") {
                  return (
                    <ExpandableThought
                      key={idx}
                      time={block.time}
                      content={block.content}
                    />
                  );
                }
                if (block.type === "text") {
                  const key = `${msg.id}-${idx}`;
                  return (
                    <div
                      key={key}
                      className="text-[14px] leading-relaxed text-gray-800 mb-3 anim-fade-in"
                    >
                      <MarkdownRenderer content={block.content} theme="light" />
                    </div>
                  );
                }
                if (block.type === "questionnaire") {
                  return (
                    <QuestionnaireWidget
                      key={idx}
                      onSubmitSuccess={handleQuestionnaireSubmit}
                      isAlreadySubmitted={layout === "split"}
                    />
                  );
                }
                if (block.type === "market-assist") {
                  return (
                    <MarketAssistPanel
                      key={idx}
                      assistId={block.assistId}
                      payload={block.payload}
                      isLocked={!!lockedMarketAssistIds[block.assistId]}
                      onLock={lockMarketAssist}
                      onConfirm={(result) => {
                        startWorkflowBuild(result);
                      }}
                    />
                  );
                }
                if (block.type === "tool") {
                  const isLastGroup = msg.id === lastAiGroupId;
                  const isLastVisible = idx === visibleCount - 1;
                  const isActive = isStreaming && isLastGroup && isLastVisible;
                  return <ToolCall key={idx} text={block.content} toolName={block.toolName} isActive={isActive} />;
                }
                return null;
              })}

              {isStreaming && msg.id === lastAiGroupId && (
                <div className="flex items-center gap-2 text-[13px] text-gray-400 mt-2 anim-fade-in">
                  <Loader2 size={14} className="animate-spin" /> Working on next
                  steps...
                </div>
              )}
            </div>
          </div>
        );
      }

      return null;
    });
  };

  return (
    <div className="h-full w-full bg-[#f9fafb] text-gray-900 font-sans flex overflow-hidden">
      <style>{`
        .scrollbar-hide::-webkit-scrollbar { display: none; }
        .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
        @keyframes zoomIn { from { opacity: 0; transform: scale(0.96); } to { opacity: 1; transform: scale(1); } }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideUp { from { opacity: 0; transform: translateY(30px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes slideInRight { from { opacity: 0; transform: translateX(15px); } to { opacity: 1; transform: translateX(0); } }
        @keyframes drawLine { from { stroke-dasharray: 400; stroke-dashoffset: 400; opacity: 0; } to { stroke-dasharray: 400; stroke-dashoffset: 0; opacity: 1; } }
        .anim-zoom-in { animation: zoomIn 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        .anim-fade-in { animation: fadeIn 0.4s ease-out forwards; }
        .anim-slide-up { animation: slideUp 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        .anim-slide-right { animation: slideInRight 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        .anim-draw-line { animation: drawLine 0.8s ease-out forwards; }
      `}</style>

      {layout === "center" ? (
        <div className="flex flex-col w-full h-full relative anim-fade-in">
          <main className="flex-1 overflow-y-auto w-full flex flex-col items-center pb-32">
            <div className="w-full max-w-3xl px-4 py-8">
              {renderMessages()}
              <div ref={messagesEndRef} />
            </div>
          </main>

          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-[#f9fafb] via-[#f9fafb] to-transparent pt-6 pb-6 px-4 flex justify-center z-10 pointer-events-none">
            <div className="w-full max-w-3xl relative pointer-events-auto">
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  send();
                }}
                className="relative shadow-sm rounded-2xl bg-white border border-gray-200 overflow-hidden focus-within:ring-2 focus-within:ring-blue-500 transition-all"
              >
                <textarea
                  rows={1}
                  value={inputValue}
                  onFocus={handleDemoPromptFocus}
                  onChange={(e) => {
                    setInputValue(e.target.value);
                    setIsDemoPrefillActive(false);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      send();
                    }
                  }}
                  placeholder="Message the assistant..."
                  className={
                    isDemoPrefillActive
                      ? "w-full py-4 pl-4 pr-12 bg-transparent text-[15px] text-gray-400 resize-none focus:outline-none scrollbar-hide"
                      : "w-full py-4 pl-4 pr-12 bg-transparent text-[15px] resize-none focus:outline-none scrollbar-hide"
                  }
                  style={{ minHeight: "56px" }}
                />
                <button
                  type="submit"
                  disabled={!inputValue.trim() || isInputDisabled}
                  className="absolute right-3 bottom-3 p-1.5 bg-black text-white rounded-lg hover:bg-gray-800 disabled:bg-gray-200 disabled:text-gray-400 transition-colors"
                >
                  <Send size={18} />
                </button>
              </form>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex w-full h-full anim-slide-right relative">
          <div className="flex-1 relative h-full flex">
            <div className="absolute top-6 left-6 z-30 pointer-events-none">
              <div className="flex items-center p-0.5 bg-white/80 backdrop-blur-md border border-gray-200 rounded-xl shadow-sm pointer-events-auto">
                <button
                  type="button"
                  onClick={() => setLeftPane("flow")}
                  className={
                    leftPane === "flow"
                      ? "px-4 py-1.5 text-sm font-semibold text-gray-900 bg-white rounded-lg shadow-[0_2px_8px_rgba(0,0,0,0.04)] ring-1 ring-gray-200/50 transition-all"
                      : "px-4 py-1.5 text-sm font-medium text-gray-500 hover:text-gray-900 hover:bg-gray-50/50 rounded-lg transition-colors"
                  }
                >
                  FlowStudio
                </button>
                <button
                  type="button"
                  onClick={() => setLeftPane("codegen")}
                  className={
                    leftPane === "codegen"
                      ? "px-4 py-1.5 text-sm font-semibold text-gray-900 bg-white rounded-lg shadow-[0_2px_8px_rgba(0,0,0,0.04)] ring-1 ring-gray-200/50 transition-all"
                      : "px-4 py-1.5 text-sm font-medium text-gray-500 hover:text-gray-900 hover:bg-gray-50/50 rounded-lg transition-colors"
                  }
                >
                  Code
                </button>
                <button
                  type="button"
                  onClick={() => setLeftPane("backtest")}
                  className={
                    leftPane === "backtest"
                      ? backtestStatus === "running"
                        ? "px-4 py-1.5 text-sm font-semibold text-gray-900 bg-white/70 rounded-lg shadow-[0_2px_8px_rgba(0,0,0,0.04)] ring-1 ring-gray-200/50 transition-all"
                        : "px-4 py-1.5 text-sm font-semibold text-gray-900 bg-white rounded-lg shadow-[0_2px_8px_rgba(0,0,0,0.04)] ring-1 ring-gray-200/50 transition-all"
                      : "px-4 py-1.5 text-sm font-medium text-gray-500 hover:text-gray-900 hover:bg-gray-50/50 rounded-lg transition-colors"
                  }
                >
                  <span className="inline-flex items-center gap-1.5">
                    {backtestStatus === "running" && <Loader2 size={14} className="animate-spin" />}
                    Backtest
                  </span>
                </button>
              </div>
            </div>

            {leftPane === "flow" ? (
              <FlowCanvas currentFlowStep={currentFlowStep} strategyType={strategyType} />
            ) : leftPane === "codegen" ? (
              <main className="flex-1 h-full bg-[#f6f8fa] relative overflow-hidden flex flex-col" style={{ paddingTop: "72px" }}>
                <StrategyCodeView
                  skipAnimation={!codeAnimationActiveRef.current && codeRevealedRef.current.has(
                    backtestScenario === "baseline" ? "baseline"
                    : backtestScenario === "sentiment" ? "sentiment"
                    : backtestScenario === "ai_baseline" ? "ai_trading"
                    : "ai_trading_sentiment"
                  )}
                  scenario={
                    backtestScenario === "baseline" ? "baseline"
                    : backtestScenario === "sentiment" ? "sentiment"
                    : backtestScenario === "ai_baseline" ? "ai_trading"
                    : "ai_trading_sentiment"
                  }
                  currentFlowStep={currentFlowStep}
                  onRevealComplete={() => {
                    codeAnimationActiveRef.current = false;
                    codeRevealedRef.current.add(
                      backtestScenario === "baseline" ? "baseline"
                      : backtestScenario === "sentiment" ? "sentiment"
                      : backtestScenario === "ai_baseline" ? "ai_trading"
                      : "ai_trading_sentiment"
                    );
                    pendingRevealDoneRef.current?.();
                    pendingRevealDoneRef.current = null;
                  }}
                />
              </main>
            ) : (
              <StrategiesBacktestView
                status={backtestStatus}
                scenario={backtestScenario}
                symbol={selectedSymbol}
                durationMs={BACKTEST_RUN_MS}
              />
            )}
          </div>

          {isChatCollapsed ? (
            <button
              type="button"
              aria-label="Expand chat sidebar"
              title="Expand chat"
              onClick={() => {
                const next = Math.min(
                  CHAT_SIDEBAR_MAX_WIDTH,
                  Math.max(CHAT_SIDEBAR_MIN_WIDTH, lastExpandedChatWidthRef.current ?? CHAT_SIDEBAR_DEFAULT_WIDTH)
                );
                setChatSidebarWidth(next);
                setIsChatCollapsed(false);
              }}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-40 flex items-center gap-2 rounded-l-full bg-white/90 backdrop-blur border border-gray-200 border-r-0 shadow-[0_10px_30px_rgba(0,0,0,0.10)] px-3 py-2 text-[13px] font-semibold text-gray-700 hover:text-gray-900 hover:bg-white transition-colors"
            >
              <ChevronLeft size={16} />
              Chat
            </button>
          ) : (
            <aside
              className="h-full bg-white border-l border-gray-200 flex flex-col shadow-[-4px_0_24px_rgba(0,0,0,0.03)] z-20 shrink-0 relative"
              style={{ width: chatSidebarWidth }}
            >
              <div
                role="separator"
                aria-orientation="vertical"
                title="Drag to resize"
                onPointerDown={(e) => {
                  if (e.button !== 0) return;
                  e.preventDefault();
                  e.stopPropagation();
                  chatResizeRef.current = {
                    startX: e.clientX,
                    startWidth: chatSidebarWidthRef.current,
                    lastWidth: chatSidebarWidthRef.current,
                  };
                  setIsChatResizing(true);
                }}
                className="absolute left-0 top-0 h-full w-3 cursor-col-resize touch-none z-30"
              >
                <div
                  className={
                    isChatResizing
                      ? "absolute left-1 top-0 h-full w-px bg-blue-500"
                      : "absolute left-1 top-0 h-full w-px bg-gray-200 hover:bg-blue-400"
                  }
                />
              </div>

              <div className="h-14 border-b border-gray-100 flex items-center px-4 shrink-0 bg-white/95 backdrop-blur-sm z-10 justify-between">
                <span className="text-sm font-semibold text-gray-700">Co-pilot Chat</span>
                <button
                  type="button"
                  onClick={() => setIsChatCollapsed(true)}
                  className="text-xs font-medium text-gray-500 hover:text-gray-800 px-2 py-1 rounded-md hover:bg-gray-50 transition-colors"
                >
                  Collapse
                </button>
              </div>

              <div className="flex-1 overflow-y-auto px-5 py-6 scrollbar-hide">
                {renderMessages()}
                <div ref={messagesEndRef} />
              </div>

              <div className="p-4 bg-white border-t border-gray-100 shrink-0">
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    send();
                  }}
                  className="relative bg-[#f4f4f5] rounded-2xl border border-transparent focus-within:border-gray-300 focus-within:bg-white focus-within:shadow-sm transition-all overflow-hidden"
                >
                  <textarea
                    rows={1}
                    value={inputValue}
                    onFocus={handleDemoPromptFocus}
                    onChange={(e) => {
                      setInputValue(e.target.value);
                      setIsDemoPrefillActive(false);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        send();
                      }
                    }}
                    placeholder="Message the assistant..."
                    className={
                      isDemoPrefillActive
                        ? "w-full py-3.5 pl-4 pr-12 bg-transparent text-[14px] text-gray-400 resize-none focus:outline-none scrollbar-hide"
                        : "w-full py-3.5 pl-4 pr-12 bg-transparent text-[14px] resize-none focus:outline-none scrollbar-hide"
                    }
                    style={{ minHeight: "52px" }}
                  />
                  <button
                    type="submit"
                    disabled={!inputValue.trim() || isInputDisabled}
                    className="absolute right-2 bottom-2.5 p-1.5 bg-black text-white rounded-xl hover:bg-gray-800 disabled:bg-gray-300 disabled:text-white transition-colors"
                  >
                    <Send size={16} />
                  </button>
                </form>
              </div>
            </aside>
          )}
        </div>
      )}
    </div>
  );
}
